import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Upload, Plus, Trash2, ExternalLink,
  Loader2, Search, BookOpen,
  Eye, EyeOff, Unlock, Archive, RefreshCw,
  Star,
  X
} from "lucide-react";
import { api, ApiError, type AdminResource } from "../../lib/api";
import { AdminFilterBar, AdminPageHeader, AdminTabBar } from "../../components/admin/AdminUI";
import { ConfirmDialog } from "../../components/admin/AdminModals";
import { AdminManualContentModal } from "../../components/admin/AdminManualContentModal";
import { AdminChapterSplitModal } from "../../components/admin/AdminChapterSplitModal";
import { AdminModuleDraftModal } from "../../components/admin/AdminModuleDraftModal";
import { Button } from "../../components/ui/Button";
import { Modal } from "../../components/ui/Modal";
import { ResourcesList } from "../../components/admin/ResourcesList";
import { BulkBar } from "../../components/admin/dataTable/BulkBar";
import { AdvancedFilters, type AdvancedField } from "../../components/admin/dataTable/AdvancedFilters";
import { adminResourceOpsApi, type BulkOpAction } from "../../lib/adminResourceOpsApi";
import { ConfirmDialog as UiConfirmDialog } from "../../components/ui/ConfirmDialog";
import { toast } from "../../components/ui/Toast";
import { useIngestedMarkers } from "../../lib/useIngestedMarkers";
import { useAdminToken, PageError } from "./adminShared";

const UPLOAD_RESOURCE_TYPES = [
  { value: "module", label: "Module (free)", hint: "Course PDF. After upload, use the Telegram bot to share the file with students." },
  { value: "previous_exam", label: "Past exam (premium)", hint: "Multiple pages of the same exam are merged into one resource and saved as a single album in the channel." },
  { value: "teacher_resource", label: "Teacher Resource", hint: "Teacher handout, reference sheet, or supplementary reading." },
] as const;

function titleFromFileName(filename: string): string {
  return filename.replace(/\.[^/.]+$/, "").replace(/[_-]+/g, " ").trim();
}

type UploadItem = {
  id: string;
  file: File;
  title: string;
};

function uploadItemId(file: File): string {
  return `${file.name}-${file.size}-${file.lastModified}`;
}

export function AdminResources() {
  const token = useAdminToken();
  const ingested = useIngestedMarkers(token);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [resources, setResources] = useState<AdminResource[]>([]);
  const [selectedResources, setSelectedResources] = useState<Set<string>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [typeTab, setTypeTab] = useState(() => searchParams.get("type") || "all");
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get("status") || "all");
  const [accessFilter, setAccessFilter] = useState(() => searchParams.get("access") || "all");
  const [streamFilter, setStreamFilter] = useState(() => searchParams.get("stream") || "all");
  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [debounced, setDebounced] = useState(() => searchParams.get("search") || "");
  const [botUsername, setBotUsername] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [adv, setAdv] = useState<Record<string, string>>({});
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const advFields = useMemo<AdvancedField[]>(() => {
    const subjects = Array.from(new Set(resources.map((r) => r.subject).filter(Boolean))).sort();
    return [
      { key: "subject", label: "Subject", type: "select", options: subjects.map((x) => ({ value: x, label: x })) },
      { key: "attention", label: "Needs attention", type: "select", options: [
        { value: "failed", label: "Failed" },
        { value: "review", label: "Needs review or publish" },
        { value: "processing", label: "Processing" },
      ] },
      { key: "published", label: "Published", type: "select", options: [{ value: "yes", label: "Published" }, { value: "no", label: "Not published" }] },
      { key: "minViews", label: "Minimum views", type: "text", placeholder: "e.g. 10" },
      { key: "from", label: "Uploaded after", type: "date" },
      { key: "to", label: "Uploaded before", type: "date" },
    ];
  }, [resources]);

  const visibleRows = useMemo(() => {
    const minViews = Number(adv.minViews);
    const from = adv.from ? Date.parse(adv.from) : NaN;
    const to = adv.to ? Date.parse(adv.to) + 86_400_000 : NaN;
    return resources.filter((r) => {
      if (adv.subject && r.subject !== adv.subject) return false;
      if (adv.published === "yes" && !(r.isPublished || r.extractionStatus === "published")) return false;
      if (adv.published === "no" && (r.isPublished || r.extractionStatus === "published")) return false;
      const failed = r.ingestionStatus === "failed" || r.extractionStatus === "failed";
      const review = r.extractionStatus === "extracted" || (r.resourceType === "previous_exam" && r.ingestionStatus === "completed" && !r.isPublished);
      const processing = ["pending", "processing"].includes(r.ingestionStatus) || ["queued", "running"].includes(r.extractionStatus ?? "");
      if (adv.attention === "failed" && !failed) return false;
      if (adv.attention === "review" && !review) return false;
      if (adv.attention === "processing" && !processing) return false;
      if (Number.isFinite(minViews) && adv.minViews && (r.views ?? 0) < minViews) return false;
      const d = Date.parse(r.date);
      if (!Number.isNaN(from) && !Number.isNaN(d) && d < from) return false;
      if (!Number.isNaN(to) && !Number.isNaN(d) && d > to) return false;
      return true;
    });
  }, [resources, adv]);

  async function runOps(action: BulkOpAction) {
    if (!token || selectedResources.size === 0) return;
    setBulkActionLoading(true);
    try {
      const res = await adminResourceOpsApi.bulk(token, Array.from(selectedResources), action);
      toast.success(`${res.count} updated${res.skipped.length ? `, ${res.skipped.length} skipped` : ""}`);
      setSelectedResources(new Set());
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Bulk action failed");
    } finally {
      setBulkActionLoading(false);
    }
  }

  async function runBulkResourceAction(action: "delete" | "make_free" | "make_premium" | "reingest" | "publish" | "unpublish") {
    if (!token || selectedResources.size === 0) return;
    setBulkActionLoading(true);
    try {
      await api.admin.bulkResourcesAction(token, {
        action,
        resource_ids: Array.from(selectedResources),
      });
      setSelectedResources(new Set());
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Bulk action failed");
    } finally {
      setBulkActionLoading(false);
    }
  }
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminResource | null>(null);
  const [uploading, setUploading] = useState(false);
  const [courses, setCourses] = useState<{ id: string; name: string; code: string }[]>([]);
  const [universities, setUniversities] = useState<{ id: string; name: string }[]>([]);
  const [streams, setStreams] = useState<{ id: string; name: string }[]>([]);
  const [uploadItems, setUploadItems] = useState<UploadItem[]>([]);
  const [uploadExamTitle, setUploadExamTitle] = useState("");
  const [uploadType, setUploadType] = useState("");
  const [uploadCourseId, setUploadCourseId] = useState("");
  const [uploadUniId, setUploadUniId] = useState("");
  const [uploadStreamId, setUploadStreamId] = useState("");
  const [uploadExpectedChapterCount, setUploadExpectedChapterCount] = useState("");
  const [uploadExpectedChapters, setUploadExpectedChapters] = useState("");
  const [chapterSplitResourceId, setChapterSplitResourceId] = useState<string | null>(null);
  const [uploadPhase, setUploadPhase] = useState<"form" | "processing" | "done" | "failed">("form");
  const [uploadError, setUploadError] = useState("");
  const [uploadFormError, setUploadFormError] = useState("");
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number; name: string } | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  const [, setReingesting] = useState<string | null>(null);
  const [, setStopping] = useState<string | null>(null);
  const [, setContinuing] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<AdminResource | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editCourseId, setEditCourseId] = useState("");
  const [editUniId, setEditUniId] = useState("");
  const [editStreamId, setEditStreamId] = useState("");
  const [editFree, setEditFree] = useState(true);
  const [editYear, setEditYear] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [showInjectModal, setShowInjectModal] = useState(false);
  const [extractConfirmTarget, setExtractConfirmTarget] = useState<AdminResource | null>(null);
  const [extractingId, setExtractingId] = useState<string | null>(null);
  const [reviewDraftModuleId, setReviewDraftModuleId] = useState<string | null>(null);

  async function handleStartExtraction(r: AdminResource) {
    if (!token) return;
    setExtractingId(r.id);
    try {
      await api.admin.extractModule(token, r.id);
      setExtractConfirmTarget(null);
      load({ silent: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to start extraction");
    } finally {
      setExtractingId(null);
    }
  }

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (debounced) next.set("search", debounced);
    if (typeTab !== "all") next.set("type", typeTab);
    if (statusFilter !== "all") next.set("status", statusFilter);
    if (accessFilter !== "all") next.set("access", accessFilter);
    if (streamFilter !== "all") next.set("stream", streamFilter);
    setSearchParams(next, { replace: true });
  }, [debounced, typeTab, statusFilter, accessFilter, streamFilter, setSearchParams]);

  const hasFilters = Boolean(search || statusFilter !== "all" || accessFilter !== "all" || streamFilter !== "all");

  function resetFileInput() {
    setUploadItems([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  const isExamBatch = uploadType === "previous_exam" && uploadItems.length > 1;

  const canProcess = Boolean(
    uploadItems.length > 0
    && uploadType
    && uploadCourseId
    && !uploading
    && !(catalogLoading && courses.length === 0)
    && (
      isExamBatch
        ? uploadExamTitle.trim()
        : uploadItems.every((item) => item.title.trim())
    ),
  );

  function openUploadModal() {
    setShowModal(true);
    setUploadPhase("form");
    setUploadError("");
    setUploadFormError("");
    resetFileInput();
    setUploadExamTitle("");
    setUploadType("");
    setUploadUniId("");
    setUploadStreamId("");
    setUploadProgress(null);
  }

  const selectedTypeHint = UPLOAD_RESOURCE_TYPES.find((t) => t.value === uploadType)?.hint ?? "";

  const loadCatalog = useCallback(() => {
    setCatalogLoading(true);
    setCatalogError("");
    Promise.allSettled([api.courses(), api.universities(), api.streams()]).then(([coursesResult, universitiesResult, streamsResult]) => {
      if (coursesResult.status === "fulfilled") {
        setCourses(coursesResult.value);
        if (coursesResult.value.length === 0) {
          setCatalogError("No courses found in catalog.");
        }
      } else {
        const err = coursesResult.reason;
        setCatalogError(err instanceof ApiError ? err.message : "Could not load courses");
      }
      if (universitiesResult.status === "fulfilled") {
        setUniversities(universitiesResult.value);
      }
      if (streamsResult.status === "fulfilled") {
        setStreams(streamsResult.value);
      }
    }).finally(() => setCatalogLoading(false));
  }, []);

  const load = useCallback((opts?: { silent?: boolean; resetSelection?: boolean }) => {
    if (!token) return;
    if (!opts?.silent) setLoading(true);
    api.admin.resources(token, {
      search: debounced,
      type: typeTab,
      status: statusFilter,
      access: accessFilter,
      stream: streamFilter !== "all" ? streamFilter : undefined,
    })
      .then((data) => {
        setResources(data.items);
        setTotal(data.total);
        setCounts(data.counts ?? {});
        setBotUsername(data.botUsername);
        if (opts?.resetSelection) {
          setSelectedResources(new Set());
        }
        setError("");
      })
      .catch((e) => {
        if (!opts?.silent) setError(e instanceof ApiError ? e.message : "Failed to load resources");
      })
      .finally(() => setLoading(false));
  }, [token, debounced, typeTab, statusFilter, accessFilter, streamFilter]);

  useEffect(() => { load({ resetSelection: true }); }, [debounced, typeTab, statusFilter, accessFilter, streamFilter, token]);

  const hasProcessingItems = useMemo(
    () => resources.some((r) => r.ingestionStatus === "pending" || r.ingestionStatus === "processing"),
    [resources]
  );

  useEffect(() => {
    if (!token || !hasProcessingItems) return;
    const id = window.setInterval(() => load({ silent: true, resetSelection: false }), 10000);
    return () => window.clearInterval(id);
  }, [token, hasProcessingItems, load]);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  useEffect(() => {
    if (courses.length === 0) return;
    setUploadCourseId((prev) => {
      if (prev && courses.some((c) => c.id === prev)) return prev;
      return courses[0].id;
    });
  }, [courses]);

  useEffect(() => {
    if (!showModal) return;
    loadCatalog();
  }, [showModal, loadCatalog]);

  useEffect(() => {
    if (!showModal) {
      setUploadPhase("form");
      setUploadError("");
    }
  }, [showModal]);

  async function pollIngestion(resourceId: string, maxAttempts = 30): Promise<{
    ingestionStatus: string;
    ingestionError?: string | null;
  }> {
    if (!token) return { ingestionStatus: "failed", ingestionError: "Not signed in" };
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      const detail = await api.admin.getResource(token, resourceId);
      if (detail.ingestionStatus === "completed" || detail.ingestionStatus === "failed" || detail.ingestionStatus === "cancelled") {
        return detail;
      }
    }
    return { ingestionStatus: "processing" };
  }

  async function uploadExamBatch(prepared: { item: UploadItem; bytes: ArrayBuffer }[]): Promise<void> {
    if (!token) throw new ApiError(401, "Not signed in");
    const form = new FormData();
    for (const { item, bytes } of prepared) {
      form.append(
        "files",
        new Blob([bytes], { type: item.file.type || "application/octet-stream" }),
        item.file.name,
      );
    }
    form.append("title", uploadExamTitle.trim());
    form.append("course_id", uploadCourseId);
    form.append("resource_type", "previous_exam");
    if (uploadUniId) form.append("university_id", uploadUniId);
    if (uploadStreamId) form.append("stream_id", uploadStreamId);

    const created = await api.admin.uploadExamBatch(token, form);
    const result = await pollIngestion(created.id);
    if (result.ingestionStatus === "failed") {
      throw new ApiError(400, result.ingestionError || "Extraction failed");
    }
    load({ silent: true });
  }

  async function uploadOne(item: UploadItem, fileBytes: ArrayBuffer): Promise<void> {
    if (!token) throw new ApiError(401, "Not signed in");

    const form = new FormData();
    form.append(
      "file",
      new Blob([fileBytes], { type: item.file.type || "application/octet-stream" }),
      item.file.name,
    );
    form.append("title", item.title.trim());
    form.append("course_id", uploadCourseId);
    form.append("resource_type", uploadType);
    if (uploadUniId) form.append("university_id", uploadUniId);
    if (uploadStreamId) form.append("stream_id", uploadStreamId);
    if (uploadType === "module") {
      if (uploadExpectedChapterCount.trim()) {
        form.append("expected_chapter_count", uploadExpectedChapterCount.trim());
      }
      if (uploadExpectedChapters.trim()) {
        form.append("expected_chapters", uploadExpectedChapters.trim());
      }
    }

    const created = await api.admin.uploadResource(token, form);
    const result = await pollIngestion(created.id);
    if (result.ingestionStatus === "failed") {
      throw new ApiError(400, result.ingestionError || "Extraction failed");
    }
    load({ silent: true });
    if (uploadType === "module") {
      setChapterSplitResourceId(created.id);
    }
  }

  async function handleProcess() {
    if (!canProcess) return;
    setUploadFormError("");
    if (!token) {
      setUploadFormError("You are not signed in. Open the admin panel again from /auth.");
      return;
    }
    if (!uploadType) {
      setUploadFormError("Select what you are uploading (module, teacher resource, or past exam).");
      return;
    }
    if (!uploadCourseId) {
      setUploadFormError("Select a course from the catalog.");
      return;
    }
    if (uploadItems.length === 0) {
      setUploadFormError("Choose at least one file.");
      return;
    }
    if (uploadItems.some((item) => !item.title.trim()) && !isExamBatch) {
      setUploadFormError("Every file needs a title.");
      return;
    }
    if (isExamBatch && !uploadExamTitle.trim()) {
      setUploadFormError("Enter a title for this past exam.");
      return;
    }

    const prepared: { item: UploadItem; bytes: ArrayBuffer }[] = [];
    for (const item of uploadItems) {
      if (!item.file.size) {
        setUploadFormError(`"${item.file.name}" is empty. Remove it or pick another file.`);
        return;
      }
      try {
        const bytes = await item.file.arrayBuffer();
        if (!bytes.byteLength) {
          setUploadFormError(`"${item.file.name}" could not be read. Open it locally first (OneDrive files).`);
          return;
        }
        prepared.push({ item, bytes });
      } catch {
        setUploadFormError(`Could not read "${item.file.name}". Open it on your computer first, then try again.`);
        return;
      }
    }

    setUploading(true);
    setUploadError("");
    setUploadPhase("processing");

    if (isExamBatch) {
      setUploadProgress({ current: 0, total: prepared.length, name: "Merging exam pages…" });
      try {
        await uploadExamBatch(prepared);
        setUploadProgress(null);
        load();
        setUploadPhase("done");
        setTimeout(() => {
          setShowModal(false);
          resetFileInput();
          setUploadExamTitle("");
          setUploadPhase("form");
        }, 1500);
      } catch (err) {
        setUploadPhase("failed");
        setUploadError(err instanceof ApiError ? err.message : "Upload failed");
      } finally {
        setUploadProgress(null);
        setUploading(false);
      }
      return;
    }

    const failures: string[] = [];

    for (let i = 0; i < prepared.length; i++) {
      const { item, bytes } = prepared[i]!;
      setUploadProgress({ current: i + 1, total: prepared.length, name: item.file.name });
      try {
        await uploadOne(item, bytes);
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : "Upload failed";
        failures.push(`${item.file.name}: ${msg}`);
      }
    }

    setUploadProgress(null);
    load();

    if (failures.length === 0) {
      setUploadPhase("done");
      setTimeout(() => {
        setShowModal(false);
        resetFileInput();
        setUploadPhase("form");
      }, 1500);
    } else if (failures.length === prepared.length) {
      setUploadPhase("failed");
      setUploadError(failures.join("\n"));
    } else {
      setUploadPhase("failed");
      setUploadError(
        `${prepared.length - failures.length} of ${prepared.length} processed.\n\nFailed:\n${failures.join("\n")}`,
      );
    }
    setUploading(false);
  }

  function handleFilePick(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    if (picked.length === 0) return;

    setUploadItems((prev) => {
      const byId = new Map(prev.map((item) => [item.id, item]));
      for (const file of picked) {
        const id = uploadItemId(file);
        byId.set(id, { id, file, title: byId.get(id)?.title ?? titleFromFileName(file.name) });
      }
      const next = Array.from(byId.values());
      if (uploadType === "previous_exam" && next.length > 1 && !uploadExamTitle.trim()) {
        setUploadExamTitle(titleFromFileName(next[0]!.file.name));
      }
      return next;
    });
    setUploadFormError("");
  }

  function updateItemTitle(id: string, title: string) {
    setUploadItems((prev) => prev.map((item) => (item.id === id ? { ...item, title } : item)));
  }

  function removeItem(id: string) {
    setUploadItems((prev) => prev.filter((item) => item.id !== id));
  }

  function handleUploadTryAgain() {
    setUploadPhase("form");
    setUploadError("");
    setUploadFormError("");
    setUploadProgress(null);
  }

  function handleView(id: string) {
    navigate(`/admin/resources/${id}${location.search || ""}`);
  }

  async function handleReingest(id: string) {
    if (!token) return;
    setStatusFilter("all");
    setReingesting(id);
    setError("");
    try {
      await api.admin.reingestResource(token, id);
      load({ silent: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Re-process failed");
    } finally {
      setReingesting(null);
    }
  }

  async function handleStop(id: string) {
    if (!token) return;
    setStopping(id);
    setError("");
    try {
      await api.admin.stopIngestion(token, id);
      load({ silent: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not stop processing");
    } finally {
      setStopping(null);
    }
  }

  async function handleContinue(id: string) {
    if (!token) return;
    setStatusFilter("all");
    setContinuing(id);
    setError("");
    try {
      await api.admin.continueIngestion(token, id);
      load({ silent: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not continue processing");
    } finally {
      setContinuing(null);
    }
  }

  function openEdit(r: AdminResource) {
    setEditTarget(r);
    setEditTitle(r.title);
    setEditCourseId(r.courseId ?? "");
    setEditUniId(r.universityId ?? "");
    setEditStreamId(r.streamId ?? "");
    setEditFree(r.isFree !== false && r.access !== "Premium");
    setEditYear(r.examYear ?? "");
    setEditError("");
  }

  async function saveEdit() {
    if (!token || !editTarget) return;
    if (!editTitle.trim()) {
      setEditError("Title is required");
      return;
    }
    setEditSaving(true);
    setEditError("");
    try {
      await api.admin.updateResource(token, editTarget.id, {
        title: editTitle.trim(),
        courseId: editCourseId || undefined,
        universityId: editUniId,
        streamId: editStreamId || null,
        isFree: editFree,
        examYear: editYear.trim(),
      });
      setEditTarget(null);
      load({ silent: true });
    } catch (e) {
      setEditError(e instanceof ApiError ? e.message : "Could not save changes");
    } finally {
      setEditSaving(false);
    }
  }

  function requestDelete(resource: AdminResource) {
    setDeleteTarget(resource);
  }

  async function confirmDeleteResource() {
    if (!token || !deleteTarget) return;
    setDeleting(deleteTarget.id);
    try {
      await api.admin.deleteResource(token, deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Delete failed");
      setDeleteTarget(null);
    } finally {
      setDeleting(null);
    }
  }

  const botLink = botUsername ? `https://t.me/${botUsername.replace("@", "")}` : null;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <AdminPageHeader
        title="Resources"
        subtitle={`${counts.all ?? total} materials · upload, extract, and manage library content`}
        actions={(
          <div className="flex items-center gap-2">
            <button
              id="admin-inject-content-btn"
              type="button"
              onClick={() => setShowInjectModal(true)}
              className="flex items-center gap-2 bg-secondary text-primary border border-primary px-4 py-2 rounded-lg text-sm font-semibold hover:bg-primary hover:text-on-accent transition"
            >
              <Plus size={15} /> Inject Content
            </button>
            <button
              id="admin-upload-resource-btn"
              type="button"
              onClick={openUploadModal}
              className="flex items-center gap-2 bg-primary text-on-accent px-4 py-2 rounded-md text-sm font-semibold hover:opacity-90 transition"
            >
              <Upload size={15} /> Upload resource
            </button>
          </div>
        )}
      />
      {error && <PageError message={error} />}

      <AdminTabBar
        active={typeTab}
        onChange={setTypeTab}
        tabs={[
          { id: "all", label: "All types", count: counts.all },
          { id: "module", label: "Modules", count: counts.module },
          { id: "previous_exam", label: "Past exams", count: counts.previous_exam },
          { id: "teacher_resource", label: "Teacher Resources", count: counts.teacher_resource },
        ]}
      />

      <AdminFilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by title or subject…"
        selects={[
          {
            id: "status",
            label: "Ingestion status",
            value: statusFilter,
            options: [
              { value: "all", label: "All statuses" },
              { value: "pending", label: "Pending" },
              { value: "processing", label: "Processing" },
              { value: "completed", label: "Completed" },
              { value: "failed", label: "Failed" },
              { value: "cancelled", label: "Stopped" },
            ],
          },
          {
            id: "access",
            label: "Access",
            value: accessFilter,
            options: [
              { value: "all", label: "All access" },
              { value: "free", label: "Free" },
              { value: "premium", label: "Premium" },
            ],
          },
          {
            id: "stream",
            label: "Stream",
            value: streamFilter,
            options: [
              { value: "all", label: "All streams" },
              { value: "common", label: "Common (No stream)" },
              ...streams.map((s) => ({ value: s.id, label: s.name })),
            ],
          },
        ]}
        onSelectChange={(id, v) => {
          if (id === "status") setStatusFilter(v);
          else if (id === "access") setAccessFilter(v);
          else if (id === "stream") setStreamFilter(v);
        }}
        showClear={hasFilters || typeTab !== "all"}
        onClear={() => {
          setSearch("");
          setStatusFilter("all");
          setAccessFilter("all");
          setStreamFilter("all");
          setTypeTab("all");
        }}
      />

      <AdvancedFilters
        fields={advFields}
        values={adv}
        onChange={(k, v) => setAdv((prev) => ({ ...prev, [k]: v }))}
        onReset={() => setAdv({})}
      />

      <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
        <p>
          Showing <span className="font-semibold text-foreground">{visibleRows.length}</span> of {counts.all ?? total}
          {counts.failed ? <span className="ml-2 font-semibold text-destructive">{counts.failed} failed</span> : null}
          {(counts.processing || counts.pending) ? <span className="ml-2 font-semibold text-info">{(counts.processing ?? 0) + (counts.pending ?? 0)} in progress</span> : null}
        </p>
        {botLink && (
          <a href={botLink} target="_blank" rel="noreferrer" className="inline-flex min-h-9 items-center gap-1 font-semibold text-primary">
            Telegram bot <ExternalLink size={12} />
          </a>
        )}
      </div>

      <ResourcesList
        rows={visibleRows}
        loading={loading}
        selected={selectedResources}
        onSelectedChange={setSelectedResources}
        ingestedPast={ingested.past}
        onView={(r) => handleView(r.id)}
        onEdit={openEdit}
        onDelete={requestDelete}
        onStop={(r) => void handleStop(r.id)}
        onContinue={(r) => void handleContinue(r.id)}
        onRetry={(r) => void handleReingest(r.id)}
        onExtract={setExtractConfirmTarget}
        onReviewDraft={(r) => setReviewDraftModuleId(r.id)}
        onUpload={openUploadModal}
      />

      <BulkBar
        count={selectedResources.size}
        noun="resources"
        busy={bulkActionLoading}
        total={visibleRows.length}
        onSelectAll={() => setSelectedResources(new Set(visibleRows.map((r) => r.id)))}
        onClear={() => setSelectedResources(new Set())}
        actions={[
          { key: "show", label: "Show", icon: <Eye size={14} />, onClick: () => void runOps("show") },
          { key: "hide", label: "Hide", icon: <EyeOff size={14} />, onClick: () => void runOps("hide") },
          { key: "free", label: "Set free", icon: <Unlock size={14} />, onClick: () => void runBulkResourceAction("make_free") },
          { key: "premium", label: "Set premium", icon: <Star size={14} />, onClick: () => void runBulkResourceAction("make_premium") },
          { key: "reingest", label: "Reprocess", icon: <RefreshCw size={14} />, onClick: () => void runBulkResourceAction("reingest") },
          { key: "archive", label: "Archive", icon: <Archive size={14} />, danger: true, onClick: () => void runOps("archive") },
          { key: "delete", label: "Delete", icon: <Trash2 size={14} />, danger: true, onClick: () => setBulkDeleteOpen(true) },
        ]}
      />
      <UiConfirmDialog
        isOpen={bulkDeleteOpen}
        title={`Delete ${selectedResources.size} resources?`}
        message="They are removed permanently. Use Archive instead if you may need them again."
        variant="destructive"
        confirmText="Delete"
        loading={bulkActionLoading}
        onConfirm={async () => {
          setBulkDeleteOpen(false);
          await runBulkResourceAction("delete");
        }}
        onCancel={() => setBulkDeleteOpen(false)}
      />
      {/* Upload Resource Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Upload resource"
        size="md"
      >
        <div className="space-y-4">

          <div className="space-y-3 mb-6">
            {uploadFormError && uploadPhase === "form" && (
              <p className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">{uploadFormError}</p>
            )}
            {catalogError && uploadPhase === "form" && (
              <p className="text-sm text-warning bg-warning/10 border border-warning/30 rounded-md px-3 py-2">{catalogError}</p>
            )}
            {uploadPhase === "processing" && (
              <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground py-4">
                <Loader2 size={18} className="animate-spin text-primary" />
                {uploadProgress ? (
                  <>
                    <span>Processing {uploadProgress.current} of {uploadProgress.total}</span>
                    <span className="text-xs truncate max-w-full px-2">{uploadProgress.name}</span>
                  </>
                ) : (
                  <span>Uploading and extracting text…</span>
                )}
              </div>
            )}
            {uploadPhase === "done" && (
              <p className="text-sm text-success bg-success/10 border border-success/30 rounded-md px-3 py-2">
                ✓ {uploadItems.length > 1 ? "All resources processed successfully" : "Resource processed successfully"}
              </p>
            )}
            {uploadPhase === "failed" && uploadError && (
              <div className="space-y-2">
                <p className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2 whitespace-pre-wrap">{uploadError}</p>
                <button
                  type="button"
                  onClick={handleUploadTryAgain}
                  className="w-full py-2 border border-primary text-primary rounded-lg text-sm font-semibold"
                >
                  Try again
                </button>
              </div>
            )}
            {uploadPhase === "form" && (
              <>
                <div>
                  <label htmlFor="admin-resource-upload" className="block text-xs font-medium mb-1">
                    Files (PDF or image)
                  </label>
                  <label
                    htmlFor="admin-resource-upload"
                    className="flex flex-col items-center justify-center w-full px-4 py-6 rounded-lg border-2 border-dashed border-border cursor-pointer hover:border-primary hover:bg-secondary/40 text-center transition"
                  >
                    <Upload size={22} className="text-primary mb-2" />
                    <span className="text-sm font-medium">
                      {uploadItems.length > 0 ? "Add more files" : "Choose files"}
                    </span>
                    <span className="text-xs text-muted-foreground mt-1">
                      {uploadType === "previous_exam"
                        ? "Select multiple pages of one exam — they are merged and sent as one album to the channel"
                        : "Select one or more files — each module or teacher resource is uploaded separately"}
                    </span>
                  </label>
                  <input
                    id="admin-resource-upload"
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
                    disabled={!uploadType}
                    onChange={handleFilePick}
                    className="sr-only"
                  />
                  {!uploadType && (
                    <p className="text-xs text-warning mt-2">Choose the resource type first.</p>
                  )}
                </div>
                {uploadItems.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">
                      {uploadItems.length} file{uploadItems.length === 1 ? "" : "s"}
                      {isExamBatch ? " — one past exam" : ""}
                    </p>
                    {isExamBatch && (
                      <div>
                        <label className="block text-xs font-medium mb-1">Exam title</label>
                        <input
                          value={uploadExamTitle}
                          onChange={(e) => setUploadExamTitle(e.target.value)}
                          placeholder="e.g. AAU Course Exam 2023"
                          className="w-full px-3 py-2 rounded-lg border border-border text-sm"
                        />
                      </div>
                    )}
                    <ul className="space-y-2 rounded-lg border border-border p-2">
                      {uploadItems.map((item) => (
                        <li key={item.id} className="flex gap-2 items-start">
                          <div className="flex-1 min-w-0 space-y-1">
                            <p className="text-xs text-muted-foreground truncate" title={item.file.name}>
                              {item.file.name} ({(item.file.size / 1024).toFixed(0)} KB)
                            </p>
                            {!isExamBatch && (
                              <input
                                value={item.title}
                                onChange={(e) => updateItemTitle(item.id, e.target.value)}
                                placeholder="Title"
                                className="w-full px-2 py-1.5 rounded-md border border-border text-sm"
                              />
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeItem(item.id)}
                            className="shrink-0 w-7 h-7 rounded-md hover:bg-destructive/10 text-destructive flex items-center justify-center mt-1"
                            title="Remove"
                          >
                            <X size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium mb-1">Resource type</label>
                  <select
                    value={uploadType}
                    onChange={(e) => setUploadType(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-border text-sm bg-background text-foreground"
                  >
                    <option value="" disabled>Select a resource type</option>
                    {UPLOAD_RESOURCE_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  {selectedTypeHint && (
                    <p className="text-xs text-muted-foreground mt-1.5">{selectedTypeHint}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Course</label>
                  <select
                    value={uploadCourseId}
                    onChange={(e) => setUploadCourseId(e.target.value)}
                    disabled={catalogLoading && courses.length === 0}
                    className="w-full px-3 py-2 rounded-lg border border-border text-sm bg-background text-foreground disabled:opacity-60"
                  >
                    {catalogLoading && courses.length === 0 ? (
                      <option value="">Loading courses…</option>
                    ) : courses.length === 0 ? (
                      <option value="">No courses available</option>
                    ) : (
                      <>
                        {!uploadCourseId && <option value="">Select a course</option>}
                        {courses.map((c) => (
                          <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                        ))}
                      </>
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">University</label>
                  <select
                    value={uploadUniId}
                    onChange={(e) => setUploadUniId(e.target.value)}
                    disabled={catalogLoading}
                    className="w-full px-3 py-2 rounded-lg border border-border text-sm bg-background text-foreground disabled:opacity-60"
                  >
                    <option value="">All universities</option>
                    {universities.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Stream</label>
                  <select
                    value={uploadStreamId}
                    onChange={(e) => setUploadStreamId(e.target.value)}
                    disabled={catalogLoading}
                    className="w-full px-3 py-2 rounded-lg border border-border text-sm bg-background text-foreground disabled:opacity-60"
                  >
                    <option value="">All Streams (Common)</option>
                    {streams.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                {uploadType === "module" && (
                  <div className="space-y-3 p-3 rounded-xl bg-muted/40 border border-border">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-primary">
                      <BookOpen size={13} /> Chapter Split Options (Optional)
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">
                        Number of chapters
                      </label>
                      <input
                        type="number"
                        min={1}
                        placeholder="e.g. 5"
                        value={uploadExpectedChapterCount}
                        onChange={(e) => setUploadExpectedChapterCount(e.target.value)}
                        className="w-full px-3 py-1.5 rounded-lg border border-border text-xs bg-background text-foreground"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Highlights any mismatch if extraction differs from textbook count.
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">
                        Known titles & page ranges
                      </label>
                      <textarea
                        rows={2}
                        placeholder={"Unit 1: Introduction (pages 1-20)\nUnit 2: Matrices (pages 21-45)"}
                        value={uploadExpectedChapters}
                        onChange={(e) => setUploadExpectedChapters(e.target.value)}
                        className="w-full px-3 py-1.5 rounded-lg border border-border text-xs bg-background text-foreground font-mono"
                      />
                    </div>
                  </div>
                )}
                <Button
                  type="button"
                  onClick={handleProcess}
                  disabled={!canProcess}
                  loading={uploading}
                  loadingText="Processing & Extracting…"
                  success={(uploadPhase as string) === "done"}
                  successText="Processed Successfully!"
                  error={(uploadPhase as string) === "failed"}
                  errorText="Upload Failed"
                  variant="primary"
                  size="md"
                  className="w-full py-2.5"
                >
                  Process{isExamBatch ? ` (${uploadItems.length} pages → 1 exam)` : uploadItems.length > 1 ? ` (${uploadItems.length} files)` : ""}
                </Button>
                {!canProcess && uploadPhase === "form" && (
                  <p className="text-xs text-muted-foreground text-center">
                    {uploadItems.length === 0
                      ? "Choose at least one file"
                      : isExamBatch && !uploadExamTitle.trim()
                        ? "Enter an exam title"
                        : !isExamBatch && uploadItems.some((item) => !item.title.trim())
                          ? "Enter a title for each file"
                          : !uploadCourseId
                            ? "Select a course"
                            : "Fill in all fields above to enable Process"}
                  </p>
                )}
              </>
            )}
          </div>

          <div className="border-t border-border pt-4">
            <p className="text-xs font-semibold text-muted-foreground mb-2">Or upload via Telegram bot</p>
            {botLink ? (
              <a
                href={botLink}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2.5 bg-info text-info-foreground rounded-md text-sm font-semibold hover:opacity-90"
              >
                <ExternalLink size={16} /> Open @{botUsername?.replace("@", "")}
              </a>
            ) : (
              <p className="text-xs text-warning">Set TELEGRAM_BOT_USERNAME on the server for bot uploads.</p>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              Bot: tap 📤 Upload resource → pick type → send PDF/photo. Bot must be admin in the storage channel.
            </p>
          </div>
        </div>
      </Modal>

      <AdminManualContentModal
        open={showInjectModal}
        token={token}
        onClose={() => setShowInjectModal(false)}
        onDone={() => { load({ silent: true }); }}
        courses={courses}
        existingResources={resources}
      />

      <AdminChapterSplitModal
        isOpen={Boolean(chapterSplitResourceId)}
        resourceId={chapterSplitResourceId || ""}
        token={token || ""}
        onClose={() => setChapterSplitResourceId(null)}
        onConfirmed={() => { load({ silent: true }); }}
      />



      {/* Edit Resource Modal */}
      {editTarget && (
        <Modal
          isOpen={!!editTarget}
          onClose={() => setEditTarget(null)}
          title="Edit resource"
          size="md"
        >
          <div className="space-y-3">
            <div className="space-y-3">
              {editError && (
                <p className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">{editError}</p>
              )}
              <div>
                <label className="block text-xs font-medium mb-1">Title</label>
                <input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Course</label>
                <select
                  value={editCourseId}
                  onChange={(e) => setEditCourseId(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border text-sm bg-background text-foreground"
                >
                  {courses.map((c) => (
                    <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">University</label>
                <select
                  value={editUniId}
                  onChange={(e) => setEditUniId(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border text-sm bg-background text-foreground"
                >
                  <option value="">All universities</option>
                  {universities.map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Stream</label>
                <select
                  value={editStreamId}
                  onChange={(e) => setEditStreamId(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border text-sm bg-background text-foreground"
                >
                  <option value="">All Streams (Common)</option>
                  {streams.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              {editTarget?.resourceType === "previous_exam" && (
                <div>
                  <label className="block text-xs font-medium mb-1">Exam year</label>
                  <input
                    value={editYear}
                    onChange={(e) => setEditYear(e.target.value)}
                    placeholder="2024"
                    className="w-full px-3 py-2 rounded-lg border border-border text-sm"
                  />
                </div>
              )}
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={editFree}
                  onChange={(e) => setEditFree(e.target.checked)}
                />
                Free for students
              </label>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setEditTarget(null)}
                  className="flex-1 py-2.5 border border-border rounded-lg text-sm font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveEdit}
                  disabled={editSaving}
                  className="flex-1 py-2.5 bg-primary text-on-accent rounded-lg text-sm font-semibold disabled:opacity-60"
                >
                  {editSaving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete resource?"
        message={`Permanently delete “${deleteTarget?.title}”? This cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        loading={!!deleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDeleteResource}
      />

      <ConfirmDialog
        open={!!extractConfirmTarget}
        title="Extract content from this module?"
        message="Extract content from this module? This uses AI and can take a few minutes. Nothing will be visible to students until you publish it."
        confirmLabel="Extract content"
        loading={!!extractingId}
        onClose={() => setExtractConfirmTarget(null)}
        onConfirm={async () => {
          if (extractConfirmTarget) await handleStartExtraction(extractConfirmTarget);
        }}
      />

      {reviewDraftModuleId && token && (
        <AdminModuleDraftModal
          open={!!reviewDraftModuleId}
          moduleId={reviewDraftModuleId}
          token={token}
          onClose={() => setReviewDraftModuleId(null)}
          onUpdated={() => load({ silent: true })}
        />
      )}
    </div>
  );
}
